import { useLayoutEffect, useRef, useState } from 'react';

interface Masse {
  fest: boolean;
  oben: number;
  links: number;
  breite: number;
  hoehe: number;
}

/** Behält den Platz im Layout, begrenzt die seitliche Leiste aber nicht am Dokumentende. */
export default function useFestePlatzhalterleiste(position: 'links' | 'rechts' | 'oben', abstandOben: number) {
  const seitlich = position !== 'oben';
  const ankerRef = useRef<HTMLDivElement>(null);
  const leisteRef = useRef<HTMLDivElement>(null);
  const [masse, setMasse] = useState<Masse | null>(null);

  useLayoutEffect(() => {
    let frame = 0;
    const messen = () => {
      frame = 0;
      if (window.matchMedia?.('print').matches) return;
      const anker = ankerRef.current?.getBoundingClientRect();
      const leiste = leisteRef.current?.getBoundingClientRect();
      if (!anker || !leiste || anker.width <= 0 || leiste.height <= 0) return;
      const neu = {
        fest: seitlich && anker.top <= abstandOben,
        // Vor dem Kleben beginnt die Leiste tiefer: nur den tatsächlich sichtbaren Platz nutzen.
        oben: Math.max(anker.top, abstandOben),
        links: anker.left,
        breite: anker.width,
        hoehe: leiste.height,
      };
      setMasse((alt) =>
        alt &&
        alt.fest === neu.fest &&
        alt.oben === neu.oben &&
        alt.links === neu.links &&
        alt.breite === neu.breite &&
        alt.hoehe === neu.hoehe
          ? alt
          : neu,
      );
    };
    const planen = () => {
      if (!frame) frame = window.requestAnimationFrame(messen);
    };
    messen();
    const beobachter = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(planen);
    // Ladehinweise oberhalb können verschwinden, ohne die Leiste oder ihren direkten
    // Rahmen zu vergrößern. Auch deren Vorfahren beobachten, damit der obere Rand aktuell bleibt.
    let vorfahr: HTMLElement | null = ankerRef.current;
    while (vorfahr) {
      beobachter?.observe(vorfahr, { box: 'border-box' });
      vorfahr = vorfahr.parentElement;
    }
    if (leisteRef.current) beobachter?.observe(leisteRef.current, { box: 'border-box' });
    window.addEventListener('scroll', planen, true);
    window.addEventListener('resize', planen);
    window.addEventListener('afterprint', planen);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      beobachter?.disconnect();
      window.removeEventListener('scroll', planen, true);
      window.removeEventListener('resize', planen);
      window.removeEventListener('afterprint', planen);
    };
  }, [seitlich, position, abstandOben]);

  return { ankerRef, leisteRef, masse: seitlich ? masse : null, oben: masse?.oben, hoehe: masse?.hoehe };
}
